import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '@modules/auth/auth.service';
import { inject } from '@angular/core';
import { catchError, map, of } from 'rxjs';
import { CompanyContextService } from '@core/context/company-context.service';
import { SubscriptionAccessService } from '@core/services/subscription-access.service';

function shouldRedirectToPricing(url: string): boolean {
  return !(
    url.startsWith('/pricing') ||
    url.startsWith('/login') ||
    url.startsWith('/register') ||
    url.startsWith('/activate-account') ||
    url.startsWith('/reset-password')
  );
}

function shouldSkipSubscriptionAccessCheck(url: string): boolean {
  return url.startsWith('/my-account');
}

export const authGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const companyContextService = inject(CompanyContextService);
  const subscriptionAccessService = inject(SubscriptionAccessService);
  const router = inject(Router);

  return authService.restoreSessionIfPossible({
    skipAccessCheck: shouldSkipSubscriptionAccessCheck(state.url),
  }).pipe(
    map((isAuthenticated) => {
      if (!isAuthenticated) {
        if (subscriptionAccessService.blockState() && shouldRedirectToPricing(state.url)) {
          return router.createUrlTree(['/pricing'], {
            queryParams: { from: state.url },
          });
        }

        return router.createUrlTree(['/login'], {
          queryParams: { redirect: state.url },
        });
      }

      if (authService.currentUser?.change_password) {
        return router.createUrlTree(['/reset-password']);
      }

      if (!companyContextService.value && state.url !== '/register') {
        return router.createUrlTree(['/register']);
      }

      return true;
    }),
    catchError(() => {
      if (subscriptionAccessService.blockState() && shouldRedirectToPricing(state.url)) {
        return of(
          router.createUrlTree(['/pricing'], {
            queryParams: { from: state.url },
          }),
        );
      }

      return of(
        router.createUrlTree(['/login'], {
          queryParams: { redirect: state.url },
        }),
      );
    }),
  );
};
